import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, ArrowRight, Loader2, Info } from "lucide-react";
import { sendSlackMessage } from "../../models/notifications";

// ─── constants ───────────────────────────────────────────────────────────────

const EASE = [0.22, 1, 0.36, 1];
const DURATION = 0.3;

// Brand tokens
const C = {
  bg: "#ffffff",
  surface: "#f6f5ef",
  border: "#e9e4dc",
  borderFocus: "#16a34a",
  accent: "#16a34a",
  accentLight: "#dcfce7",
  textPrimary: "#2f2f2f",
  textSecondary: "#7f7e7c",
  textMuted: "#a6a4a2",
  selectedBg: "#16a34a",
  selectedText: "#ffffff",
};

// ─── routing logic ───────────────────────────────────────────────────────────

function routeLead(f) {
  const FIT_CATEGORIES = ["apparel", "footwear", "beauty", "furniture"];
  if (f.refund_today === "no_returns") return "wrong";
  if (f.monthly_revenue === "lt_250k") return "wrong";
  if (f.monthly_revenue === "250k_800k") return "early";
  if (!FIT_CATEGORIES.includes(f.category)) return "early";
  if (f.return_rate === "lt_10") return "early";
  return "fit";
}

// ─── sub-components ──────────────────────────────────────────────────────────

function ProgressBar({ step }) {
  return (
    <div className="mb-7">
      <div className="flex gap-1.5 mb-2.5">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-[3px] flex-1 rounded-full overflow-hidden"
            style={{ background: C.border }}
          >
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: i <= step ? "100%" : "0%",
                background: i <= step ? C.accent : "transparent",
              }}
            />
          </div>
        ))}
      </div>
      <p className="text-xs" style={{ color: C.textMuted }}>
        Step {step + 1} of 3
      </p>
    </div>
  );
}

function FieldLabel({ children, tooltip }) {
  const [show, setShow] = useState(false);
  return (
    <div className="flex items-center gap-1.5 mb-2">
      <span className="text-sm font-medium" style={{ color: C.textSecondary }}>
        {children}
      </span>
      {tooltip && (
        <div className="relative flex items-center">
          <button
            type="button"
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
            onFocus={() => setShow(true)}
            onBlur={() => setShow(false)}
            className="transition-colors"
            style={{ color: C.textMuted }}
          >
            <Info size={13} />
          </button>
          {show && (
            <div
              className="absolute left-5 top-1/2 -translate-y-1/2 z-10 w-48 sm:w-60 rounded-xl px-3.5 py-2.5 text-xs leading-relaxed shadow-lg"
              style={{
                background: C.bg,
                border: `1px solid ${C.border}`,
                color: C.textSecondary,
              }}
            >
              {tooltip}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DarkInput({ type = "text", value, onChange, placeholder }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-xl px-4 py-3 text-sm outline-none transition-colors"
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        color: C.textPrimary,
      }}
      onFocus={(e) => {
        e.target.style.borderColor = C.borderFocus;
        e.target.style.background = C.bg;
      }}
      onBlur={(e) => {
        e.target.style.borderColor = C.border;
        e.target.style.background = C.surface;
      }}
    />
  );
}

function DarkSelect({ value, onChange, options, placeholder = "Select…" }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl px-4 py-3 text-sm outline-none appearance-none cursor-pointer transition-colors"
        style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          color: value ? C.textPrimary : C.textMuted,
        }}
        onFocus={(e) => {
          e.target.style.borderColor = C.borderFocus;
          e.target.style.background = C.bg;
        }}
        onBlur={(e) => {
          e.target.style.borderColor = C.border;
          e.target.style.background = C.surface;
        }}
      >
        <option value="" disabled style={{ color: C.textMuted }}>
          {placeholder}
        </option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} style={{ color: C.textPrimary }}>
            {opt.label}
          </option>
        ))}
      </select>
      <div
        className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2"
        style={{ color: C.textMuted }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M2.5 4.5L6 8L9.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}

function ButtonGroup({ options, value, onChange, cols = 4 }) {
  const desktopCols =
    cols === 5
      ? "sm:grid-cols-5"
      : cols === 4
      ? "sm:grid-cols-4"
      : "sm:grid-cols-3";

  return (
    <div className={`grid grid-cols-2 ${desktopCols} gap-2`}>
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className="rounded-xl px-3 py-2.5 text-sm text-center transition-all"
            style={
              selected
                ? {
                    background: C.selectedBg,
                    color: C.selectedText,
                    fontWeight: 600,
                    border: `1px solid ${C.selectedBg}`,
                  }
                : {
                    background: C.surface,
                    color: C.textSecondary,
                    border: `1px solid ${C.border}`,
                  }
            }
            onMouseEnter={(e) => {
              if (!selected) {
                e.currentTarget.style.borderColor = C.accent;
                e.currentTarget.style.color = C.textPrimary;
              }
            }}
            onMouseLeave={(e) => {
              if (!selected) {
                e.currentTarget.style.borderColor = C.border;
                e.currentTarget.style.color = C.textSecondary;
              }
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function StackedList({ options, value, onChange }) {
  return (
    <div className="flex flex-col gap-2">
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className="w-full rounded-xl px-4 py-3 text-sm text-left transition-all"
            style={
              selected
                ? {
                    background: C.selectedBg,
                    color: C.selectedText,
                    fontWeight: 600,
                    border: `1px solid ${C.selectedBg}`,
                  }
                : {
                    background: C.surface,
                    color: C.textSecondary,
                    border: `1px solid ${C.border}`,
                  }
            }
            onMouseEnter={(e) => {
              if (!selected) {
                e.currentTarget.style.borderColor = C.accent;
                e.currentTarget.style.color = C.textPrimary;
              }
            }}
            onMouseLeave={(e) => {
              if (!selected) {
                e.currentTarget.style.borderColor = C.border;
                e.currentTarget.style.color = C.textSecondary;
              }
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

export default function ManagedEconomyForm({ onStepChange }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    work_email: "",
    store_url: "",
    shopify_plan: "",
    monthly_revenue: "",
    return_rate: "",
    customer_accounts: "",
    category: "",
    category_other: "",
    refund_today: "",
    returns_platform: "",
    returns_platform_other: "",
    retention_owner: "",
    brand_goal: "",
  });

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  function isEmailValid(email) {
    const atIdx = email.indexOf("@");
    if (atIdx < 1) return false;
    return email.slice(atIdx + 1).includes(".");
  }

  const step1Valid =
    isEmailValid(form.work_email) &&
    form.store_url.includes(".") &&
    form.shopify_plan !== "";

  const step2Valid =
    form.monthly_revenue !== "" &&
    form.return_rate !== "" &&
    form.customer_accounts !== "" &&
    form.category !== "" &&
    (form.category !== "other" || form.category_other.trim() !== "");

  const step3Valid =
    form.refund_today !== "" &&
    form.returns_platform !== "" &&
    (form.returns_platform !== "other" ||
      form.returns_platform_other.trim() !== "") &&
    form.retention_owner !== "";

  const stepValid = [step1Valid, step2Valid, step3Valid];

  function goNext() {
    if (!stepValid[step]) return;
    setDirection(1);
    setStep((s) => {
      const next = s + 1;
      onStepChange?.(next);
      return next;
    });
  }

  function goBack() {
    setDirection(-1);
    setStep((s) => {
      const prev = s - 1;
      onStepChange?.(prev);
      return prev;
    });
  }

  async function handleSubmit() {
    if (!stepValid[2]) return;
    setLoading(true);

    const lead_group = routeLead(form);
    const payload = {
      ...form,
      category_other: form.category_other || null,
      returns_platform_other: form.returns_platform_other || null,
      brand_goal: form.brand_goal || null,
      lead_group,
      submitted_at: new Date().toISOString(),
    };

    await new Promise((resolve) => setTimeout(resolve, 600));
    console.log("ManagedEconomyForm payload:", payload);

    const revenueLabels = {
      lt_250k: "< $250K/mo", "250k_800k": "$250K–$800K/mo",
      "800k_2m": "$800K–$2M/mo", "2m_8m": "$2M–$8M/mo", gt_8m: "> $8M/mo",
    };
    const returnRateLabels = {
      lt_10: "< 10%", "10_20": "10–20%", "20_30": "20–30%", gt_30: "> 30%", unknown: "Unknown",
    };
    const slackMessage = [
      `*New Managed Economy application* — lead group: *${lead_group.toUpperCase()}*`,
      `• Email: ${form.work_email}`,
      `• Store: ${form.store_url}`,
      `• Shopify plan: ${form.shopify_plan}`,
      `• Revenue: ${revenueLabels[form.monthly_revenue] || form.monthly_revenue}`,
      `• Return rate: ${returnRateLabels[form.return_rate] || form.return_rate}`,
      `• Category: ${form.category === "other" ? form.category_other : form.category}`,
      `• Refund method: ${form.refund_today}`,
      `• Returns platform: ${form.returns_platform === "other" ? form.returns_platform_other : form.returns_platform}`,
      `• Retention owner: ${form.retention_owner}`,
      form.brand_goal ? `• Goal: ${form.brand_goal}` : null,
    ].filter(Boolean).join("\n");

    sendSlackMessage(slackMessage, "#high-alerts");
    sendSlackMessage(slackMessage, "#website-updates");

    setLoading(false);

    if (lead_group === "fit" || lead_group === "early") {
      sessionStorage.setItem("qualified_lead", "true");
      navigate("/scheduleCall");
    } else {
      setResult("wrong");
    }
  }

  const variants = {
    enter: (dir) => ({ x: dir > 0 ? 24 : -24, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -24 : 24, opacity: 0 }),
  };

  const transition = { duration: DURATION, ease: EASE };

  // ── result states ──────────────────────────────────────────────────────────

  if (result !== null) {
    const isFitOrEarly = result === "fit" || result === "early";
    return (
      <div className="w-full max-w-[520px] mx-auto px-4 sm:px-0">
        <div
          className="p-6 sm:p-10 rounded-2xl flex flex-col items-center text-center gap-5"
          style={{
            background: C.bg,
            border: `1px solid ${C.border}`,
            borderRadius: "16px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          }}
        >
          {isFitOrEarly ? (
            <>
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center"
                style={{ background: C.accentLight }}
              >
                <CheckCircle size={20} style={{ color: C.accent }} />
              </div>
              <div>
                <p className="text-xl font-semibold mb-2" style={{ color: C.textPrimary }}>
                  Booked.
                </p>
                <p className="text-sm leading-relaxed" style={{ color: C.textSecondary }}>
                  We will send times to{" "}
                  <span style={{ color: C.textPrimary, fontWeight: 500 }}>
                    {form.work_email}
                  </span>{" "}
                  within the hour, along with a first pass at your return volume.
                </p>
              </div>
            </>
          ) : (
            <>
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center"
                style={{ background: C.surface }}
              >
                <ArrowRight size={18} style={{ color: C.textSecondary }} />
              </div>
              <div>
                <p className="text-xl font-semibold mb-2" style={{ color: C.textPrimary }}>
                  Start on a self-serve plan.
                </p>
                <p className="text-sm leading-relaxed mb-6" style={{ color: C.textSecondary }}>
                  Your store credit program can run without an operator today.
                  You can move to Managed at any time.
                </p>
                <a
                  href="/pricing"
                  className="inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-full transition-all"
                  style={{ background: C.accent, color: "#ffffff" }}
                >
                  See plans <ArrowRight size={13} />
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── form ───────────────────────────────────────────────────────────────────

  return (
    <div className="w-full px-4 sm:px-0">
      <div
        className="p-5 sm:p-9"
        style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: "16px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        }}
      >
        <ProgressBar step={step} />

        <div className="overflow-hidden relative">
          <AnimatePresence mode="wait" custom={direction}>
            {/* ── Step 1 — Identity ─────────────────────────────────────── */}
            {step === 0 && (
              <motion.div
                key="step0"
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={transition}
              >
                <div className="space-y-5">
                  <div>
                    <FieldLabel>Work email</FieldLabel>
                    <DarkInput
                      type="email"
                      value={form.work_email}
                      onChange={(v) => set("work_email", v)}
                      placeholder="you@brand.com"
                    />
                  </div>

                  <div>
                    <FieldLabel>Store URL</FieldLabel>
                    <DarkInput
                      value={form.store_url}
                      onChange={(v) => set("store_url", v)}
                      placeholder="brand.myshopify.com"
                    />
                  </div>

                  <div>
                    <FieldLabel>Shopify plan</FieldLabel>
                    <ButtonGroup
                      options={[
                        { value: "basic", label: "Basic" },
                        { value: "shopify", label: "Shopify" },
                        { value: "advanced", label: "Advanced" },
                        { value: "plus", label: "Plus" },
                      ]}
                      value={form.shopify_plan}
                      onChange={(v) => set("shopify_plan", v)}
                      cols={4}
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── Step 2 — Scale ────────────────────────────────────────── */}
            {step === 1 && (
              <motion.div
                key="step1"
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={transition}
              >
                <div className="space-y-5">
                  <div>
                    <FieldLabel>Monthly revenue</FieldLabel>
                    <DarkSelect
                      value={form.monthly_revenue}
                      onChange={(v) => set("monthly_revenue", v)}
                      placeholder="Select range…"
                      options={[
                        { value: "lt_250k", label: "Less than $250K / mo" },
                        { value: "250k_800k", label: "$250K – $800K / mo" },
                        { value: "800k_2m", label: "$800K – $2M / mo" },
                        { value: "2m_8m", label: "$2M – $8M / mo" },
                        { value: "gt_8m", label: "More than $8M / mo" },
                      ]}
                    />
                  </div>

                  <div>
                    <FieldLabel>Return rate</FieldLabel>
                    <ButtonGroup
                      options={[
                        { value: "lt_10", label: "Less than 10%" },
                        { value: "10_20", label: "10% – 20%" },
                        { value: "20_30", label: "20% – 30%" },
                        { value: "gt_30", label: "More than 30%" },
                        { value: "unknown", label: "I do not know" },
                      ]}
                      value={form.return_rate}
                      onChange={(v) => set("return_rate", v)}
                      cols={5}
                    />
                  </div>

                  <div>
                    <FieldLabel tooltip="One shopper with a credit balance more than zero. Wallets that empty out stop counting.">
                      Active customer accounts
                    </FieldLabel>
                    <DarkSelect
                      value={form.customer_accounts}
                      onChange={(v) => set("customer_accounts", v)}
                      placeholder="Select range…"
                      options={[
                        { value: "lt_10k", label: "Less than 10,000" },
                        { value: "10k_25k", label: "10,000 – 25,000" },
                        { value: "25k_60k", label: "25,000 – 60,000" },
                        { value: "60k_200k", label: "60,000 – 200,000" },
                        { value: "gt_200k", label: "More than 200,000" },
                      ]}
                    />
                  </div>

                  <div>
                    <FieldLabel>Primary category</FieldLabel>
                    <ButtonGroup
                      options={[
                        { value: "apparel", label: "Apparel" },
                        { value: "footwear", label: "Footwear" },
                        { value: "beauty", label: "Beauty" },
                        { value: "furniture", label: "Furniture" },
                        { value: "other", label: "Other" },
                      ]}
                      value={form.category}
                      onChange={(v) => {
                        set("category", v);
                        if (v !== "other") set("category_other", "");
                      }}
                      cols={3}
                    />
                  </div>

                  {form.category === "other" && (
                    <div>
                      <FieldLabel>Which category?</FieldLabel>
                      <DarkInput
                        value={form.category_other}
                        onChange={(v) => set("category_other", v)}
                        placeholder="e.g. Electronics, Sporting goods…"
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* ── Step 3 — Operations ───────────────────────────────────── */}
            {step === 2 && (
              <motion.div
                key="step2"
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={transition}
              >
                <div className="space-y-5">
                  <div>
                    <FieldLabel>What happens to a refund today?</FieldLabel>
                    <StackedList
                      options={[
                        { value: "cash", label: "Cash back to the original payment method" },
                        { value: "gift_card", label: "A Shopify gift card" },
                        { value: "credit_app", label: "Store credit through an app" },
                        { value: "no_returns", label: "We do not accept returns" },
                      ]}
                      value={form.refund_today}
                      onChange={(v) => set("refund_today", v)}
                    />
                  </div>

                  <div>
                    <FieldLabel>Which returns platform do you use?</FieldLabel>
                    <ButtonGroup
                      options={[
                        { value: "loop", label: "Loop" },
                        { value: "returnly", label: "Returnly" },
                        { value: "narvar", label: "Narvar" },
                        { value: "aftership", label: "AfterShip" },
                        { value: "shopify", label: "Shopify native" },
                        { value: "none", label: "None" },
                        { value: "other", label: "Other" },
                      ]}
                      value={form.returns_platform}
                      onChange={(v) => {
                        set("returns_platform", v);
                        if (v !== "other") set("returns_platform_other", "");
                      }}
                      cols={4}
                    />
                  </div>

                  {form.returns_platform === "other" && (
                    <div>
                      <FieldLabel>Which one?</FieldLabel>
                      <DarkInput
                        value={form.returns_platform_other}
                        onChange={(v) => set("returns_platform_other", v)}
                        placeholder="Platform name"
                      />
                    </div>
                  )}

                  <div>
                    <FieldLabel>Who owns retention today?</FieldLabel>
                    <ButtonGroup
                      options={[
                        { value: "retention_team", label: "A retention or lifecycle team" },
                        { value: "marketing", label: "Marketing" },
                        { value: "finance", label: "Finance" },
                        { value: "support", label: "Customer support" },
                        { value: "nobody", label: "Nobody yet" },
                      ]}
                      value={form.retention_owner}
                      onChange={(v) => set("retention_owner", v)}
                      cols={3}
                    />
                  </div>

                  <div>
                    <FieldLabel>Where are you trying to take your brand?</FieldLabel>
                    <textarea
                      rows={4}
                      value={form.brand_goal}
                      onChange={(e) =>
                        set("brand_goal", e.target.value.slice(0, 500))
                      }
                      placeholder="Tell us where you're headed."
                      maxLength={500}
                      className="w-full rounded-xl px-4 py-3 text-sm outline-none resize-none transition-colors"
                      style={{
                        background: C.surface,
                        border: `1px solid ${C.border}`,
                        color: C.textPrimary,
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = C.borderFocus;
                        e.target.style.background = C.bg;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = C.border;
                        e.target.style.background = C.surface;
                      }}
                    />
                    <p className="text-xs mt-1.5 text-right" style={{ color: C.textMuted }}>
                      {form.brand_goal.length} / 500
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── button row ────────────────────────────────────────────────────── */}
        <div className="flex justify-between items-center mt-8">
          {step > 0 ? (
            <button
              type="button"
              onClick={goBack}
              disabled={loading}
              className="px-5 py-2.5 text-sm rounded-full transition-all disabled:opacity-40"
              style={{
                background: "transparent",
                border: `1px solid ${C.border}`,
                color: C.textSecondary,
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.borderColor = C.textSecondary;
                  e.currentTarget.style.color = C.textPrimary;
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = C.border;
                e.currentTarget.style.color = C.textSecondary;
              }}
            >
              Back
            </button>
          ) : (
            <div />
          )}

          {step < 2 ? (
            <button
              type="button"
              onClick={goNext}
              disabled={!stepValid[step]}
              className="px-5 py-2.5 text-sm rounded-full font-bold transition-all"
              style={
                stepValid[step]
                  ? { background: C.accent, color: "#ffffff", border: `1px solid ${C.accent}` }
                  : {
                      background: C.surface,
                      color: C.textMuted,
                      border: `1px solid ${C.border}`,
                      cursor: "not-allowed",
                    }
              }
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!stepValid[2] || loading}
              className="px-5 py-2.5 text-sm rounded-full font-bold transition-all flex items-center gap-2"
              style={
                stepValid[2] && !loading
                  ? { background: C.accent, color: "#ffffff", border: `1px solid ${C.accent}` }
                  : {
                      background: C.surface,
                      color: C.textMuted,
                      border: `1px solid ${C.border}`,
                      cursor: "not-allowed",
                    }
              }
            >
              {loading && <Loader2 size={13} className="animate-spin" />}
              {loading ? "Submitting…" : "Submit"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
